import React from 'react';
import { Input } from 'reactstrap';
import classNames from 'classnames';

import Icon, { faSort, faSortDown, faSortUp } from '~components/icon';

import ColumnHeaderTooltip from './ColumnHeaderTooltip';
import { getSortDirection, getSortTitle } from './sorter';
import { Column, Row, SortState } from './types';

interface ComponentProps {
  // The owning TableView's id, used to scope per-header tooltip target ids so two tables on the
  // same page cannot collide on them.
  tableId: string;
  showSelect: boolean;
  columns: Column[];
  showActions: boolean;
  allowSort: boolean;
  sortState: SortState;
  onSort?: (id: string) => void;
  data: Row[];
  selectedIds: string[];
  handleSelectRow: (selectedIds: string[], isBulkOperation?: boolean) => void;
  isRowDisabled?: ((row: Row) => boolean) | boolean;
}

const TableHeader: React.FC<ComponentProps> = ({
  tableId,
  showSelect,
  columns,
  showActions,
  allowSort,
  sortState,
  onSort,
  data,
  selectedIds,
  handleSelectRow,
  isRowDisabled = false,
}) => {
  // "Select all" only ever governs selectable rows - a row excluded from selection by
  // isRowDisabled (e.g. copy-ineligible on the DEX Readiness Report) never appears in
  // selectedIds, so checking against the full `data` set meant `every()` could never be true
  // and the checkbox could never be toggled off again (TAV-3133, Beth Kairys review feedback).
  const selectableIds = data
    .filter((r) => !(typeof isRowDisabled === 'function' ? isRowDisabled(r) : isRowDisabled))
    .map((r) => String(r.id));
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  return (
    <thead>
      <tr>
        {showSelect && (
          <th className={'select-cell'}>
            <Input
              type="checkbox"
              id={'select-all-checkboxes'}
              checked={allSelectableSelected}
              onChange={(e) => {
                const next = e.target.checked ? selectableIds : [];
                handleSelectRow && handleSelectRow(next, true);
              }}
              aria-label="Select all rows"
              innerRef={(el: HTMLInputElement) => {
                if (!el) return;
                // Checking selectedIds.length alone counts a stale selection (e.g. ids left over
                // from a previous page/search that don't correspond to any currently selectable
                // row) as "some selected" here, even though none of this view's selectable rows
                // actually are (CodeRabbit finding, MR !1428).
                const some = !allSelectableSelected && selectableIds.some((id) => selectedIds.includes(id));
                el.indeterminate = some;
              }}
            />
          </th>
        )}
        {columns.map((column) => {
          const { id } = column;
          const columnSortable = allowSort && column.sortable !== false;
          const className = classNames([column.classNames?.header], {
            selectable: columnSortable,
          });

          const sortDirection = getSortDirection(sortState, id);
          const sortTitle = getSortTitle(sortState, id);

          // A captioned header owns its own tooltip, so the native `title` is left off: on one
          // element both would open together on a single hover. The sort affordance is not lost -
          // it moves into that tooltip as a second line.
          const { caption } = column;

          return (
            <th key={id} className={className} style={column.style}>
              <div
                className="cell"
                title={columnSortable && !caption ? sortTitle : undefined}
                onClick={columnSortable ? () => onSort?.(id) : undefined}
              >
                {caption ? (
                  <ColumnHeaderTooltip
                    targetId={`${tableId}-header-${id}`}
                    label={column.header}
                    caption={caption}
                    sortHint={columnSortable ? sortTitle : undefined}
                  />
                ) : (
                  column.header
                )}
                {columnSortable &&
                  {
                    asc: <Icon icon={faSortUp} className="sort-icon" />,
                    desc: <Icon icon={faSortDown} className="sort-icon" />,
                    null: <Icon icon={faSort} className="sort-icon invisible" />,
                  }[sortDirection as string]}
              </div>
            </th>
          );
        })}
        {showActions && <th className="actions-cell" style={{ width: '100px' }}></th>}
      </tr>
    </thead>
  );
};

export default TableHeader;
